import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { AreaTexto } from '@/shared/ui/Formulario/AreaTexto'
import { Campo } from '@/shared/ui/Formulario/Campo'
import { Selector } from '@/shared/ui/Formulario/Selector'
import { Modal } from '@/shared/ui/Modal/Modal'
import { opcionesCiudades, opcionesDepartamentos } from '@/shared/datos/colombia'

/**
 * LO QUE MANDA CONTABILIDAD Y LO QUE MANDA OPERACIÓN.
 *
 * La pantalla de clientes dice, con razón, que los terceros se crean en World
 * Office y no aquí: el NIT y la razón social son de la contabilidad, y
 * cambiarlos por este lado dejaría las dos versiones peleadas.
 *
 * Pero de ahí se saltó a que NO SE PUEDE TOCAR NADA, y eso es otra cosa. El
 * WhatsApp al que llega el acta para la firma, la dirección donde queda la
 * obra y la zona no son datos contables: son de operación, y World Office ni
 * siquiera los guarda. Sin poder escribirlos, el acta no se puede mandar y la
 * dirección de la orden hay que teclearla entera cada vez.
 *
 * MEDIDO el día que se escribió esto: de 37 clientes, UNO tenía dirección.
 * No porque a nadie le importara, sino porque la única pantalla que deja
 * escribirla vive en una ruta que el menú no ofrece.
 *
 * Así que aquí se editan los de operación, y los de contabilidad se muestran
 * apagados con su motivo al lado. Un campo bloqueado sin explicación se lee
 * como un fallo.
 */

type Props = {
  abierto: boolean
  /** Null cuando el tercero todavía no vive en la matriz: se adopta al guardar. */
  clienteId: string | null
  idTercero: number
  nombre: string
  identificacion: string
  onCerrar: () => void
}

type Operacion = {
  ciudad: string
  departamento: string
  nombre_contacto: string
  telefono: string
  telefono_whatsapp: string
  correo: string
  direccion: string
  zona_id: string
  notas: string
}

const VACIO: Operacion = {
  ciudad: '',
  departamento: '',
  nombre_contacto: '',
  telefono: '',
  telefono_whatsapp: '',
  correo: '',
  direccion: '',
  zona_id: '',
  notas: '',
}

export function DatosDeOperacion({
  abierto,
  clienteId,
  idTercero,
  nombre,
  identificacion,
  onCerrar,
}: Props) {
  const cliente = useQueryClient()
  const [valores, setValores] = useState<Operacion>(VACIO)
  const [error, setError] = useState<string | null>(null)

  const { data: guardado } = useQuery({
    queryKey: ['cliente-operacion', clienteId],
    enabled: abierto && Boolean(clienteId),
    queryFn: async (): Promise<Operacion> => {
      const { data, error: fallo } = await supabase
        .from('clientes')
        .select(
          'ciudad, departamento, nombre_contacto, telefono, telefono_whatsapp, correo, direccion, zona_id, notas',
        )
        .eq('id', clienteId!)
        .maybeSingle()

      if (fallo) throw new Error(mensajeDeError(fallo))

      const f = (data ?? {}) as Partial<Record<keyof Operacion, string | null>>
      return {
        ciudad: f.ciudad ?? '',
        departamento: f.departamento ?? '',
        nombre_contacto: f.nombre_contacto ?? '',
        telefono: f.telefono ?? '',
        telefono_whatsapp: f.telefono_whatsapp ?? '',
        correo: f.correo ?? '',
        direccion: f.direccion ?? '',
        zona_id: f.zona_id ?? '',
        notas: f.notas ?? '',
      }
    },
  })

  // Al abrir se siembra con lo guardado. Sin esto, abrir dos clientes seguidos
  // enseñaría los datos del primero en el formulario del segundo.
  useEffect(() => {
    if (abierto) setValores(guardado ?? VACIO)
  }, [abierto, guardado])

  const guardar = useMutation({
    mutationFn: async (): Promise<void> => {
      let id = clienteId

      // Todavía no está en la matriz: se adopta ahora. Es la misma función que
      // usa el formulario de órdenes, así que el tercero entra una sola vez y
      // de una sola manera.
      if (!id) {
        const { data, error: fallo } = await supabase.rpc('fn_adoptar_cliente', {
          p_id_tercero: idTercero,
        })
        if (fallo) throw new Error(mensajeDeError(fallo))
        id = data as string
      }

      const limpio = (v: string) => (v.trim() === '' ? null : v.trim())

      const { error: fallo } = await supabase
        .from('clientes')
        .update({
          ciudad: limpio(valores.ciudad),
          departamento: limpio(valores.departamento),
          nombre_contacto: limpio(valores.nombre_contacto),
          telefono: limpio(valores.telefono),
          telefono_whatsapp: limpio(valores.telefono_whatsapp),
          correo: limpio(valores.correo),
          direccion: limpio(valores.direccion),
          notas: limpio(valores.notas),
        } as never)
        .eq('id', id)

      if (fallo) throw new Error(mensajeDeError(fallo))
    },
    onSuccess: async () => {
      await Promise.all([
        cliente.invalidateQueries({ queryKey: ['clientes-wo'] }),
        cliente.invalidateQueries({ queryKey: ['cliente-operacion'] }),
      ])
      onCerrar()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo guardar.'),
  })

  function cambiar(campo: keyof Operacion, valor: string) {
    setValores((previos) => ({ ...previos, [campo]: valor }))
  }

  return (
    <Modal
      abierto={abierto}
      ancho
      titulo="Datos de operación"
      subtitulo={`${nombre} · ${identificacion}`}
      onCerrar={onCerrar}
      pie={
        <>
          <Boton variante="plano" onClick={onCerrar} disabled={guardar.isPending}>
            Cancelar
          </Boton>
          <Boton
            variante="principal"
            onClick={() => {
              setError(null)
              guardar.mutate()
            }}
            disabled={guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Boton>
        </>
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      <Aviso tipo="info" titulo="El NIT y el nombre no se tocan aquí">
        Esos son de World Office, que es donde vive la contabilidad. Lo de abajo no lo guarda
        World Office: es de MEGA, y sin ello el acta no se puede mandar ni la dirección se
        rellena sola al crear una orden.
      </Aviso>

      {!clienteId && (
        <Aviso tipo="alerta">
          Este tercero todavía no está en la matriz. Al guardar entra, con los mismos datos que
          si se hubiera creado desde una orden.
        </Aviso>
      )}

      <Campo
        etiqueta="WhatsApp"
        type="tel"
        value={valores.telefono_whatsapp}
        onChange={(e) => cambiar('telefono_whatsapp', e.target.value)}
        ayuda="A este número llega el acta de conformidad para la firma."
      />

      {/* DEPARTAMENTO Y CIUDAD, EN CASCADA Y DE TODA COLOMBIA.
          Como en cualquier tienda en línea: primero el departamento, y la
          ciudad se llena con sus municipios. Antes la ciudad salía de una lista
          corta —solo los municipios ya usados— y el departamento ni se
          preguntaba; ahora está el país entero y los dos van relacionados, así
          nunca queda una pareja imposible —Cartago con Risaralda—. */}
      {/* DESPLEGABLE Y NO TEXTO LIBRE: de este campo depende DÓNDE se busca la
          dirección en el mapa; una letra mal puesta manda la obra a otro
          municipio, y así «Cartago» y «cartago» se contaban como dos. */}
      <Selector
        etiqueta="Departamento"
        value={valores.departamento}
        onChange={(e) => {
          const dep = e.target.value
          setValores((previos) => ({
            ...previos,
            departamento: dep,
            // La ciudad cuelga del departamento: si ya no le pertenece, se
            // suelta para no dejar una pareja que no existe.
            ciudad: opcionesCiudades(dep).some((c) => c.valor === previos.ciudad)
              ? previos.ciudad
              : '',
          }))
        }}
        opciones={opcionesDepartamentos()}
        textoVacio="Sin definir (se asume Cartago)"
        ayuda="Primero el departamento; luego se elige su ciudad."
      />

      <Selector
        etiqueta="Ciudad"
        value={valores.ciudad}
        onChange={(e) => cambiar('ciudad', e.target.value)}
        opciones={opcionesCiudades(valores.departamento)}
        textoVacio={valores.departamento ? 'Seleccione…' : 'Elija primero el departamento'}
        disabled={!valores.departamento}
        ayuda="Dónde se busca su dirección en el mapa. Sin esto se asume Cartago."
      />

      <Campo
        etiqueta="Dirección"
        value={valores.direccion}
        onChange={(e) => cambiar('direccion', e.target.value)}
        ayuda="Se propone al crear una orden. Si el trabajo es en otro sitio, allí se cambia."
      />


      <Campo
        etiqueta="Persona de contacto"
        value={valores.nombre_contacto}
        onChange={(e) => cambiar('nombre_contacto', e.target.value)}
      />

      <Campo
        etiqueta="Teléfono"
        type="tel"
        value={valores.telefono}
        onChange={(e) => cambiar('telefono', e.target.value)}
      />

      <Campo
        etiqueta="Correo"
        type="email"
        value={valores.correo}
        onChange={(e) => cambiar('correo', e.target.value)}
      />

      <AreaTexto
        etiqueta="Notas"
        value={valores.notas}
        onChange={(e) => cambiar('notas', e.target.value)}
        ayuda="Lo que hay que saber antes de ir: el portero, el horario, con quién se entiende uno."
      />
    </Modal>
  )
}
